import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  usePurchaseSubscription,
  useChangeSubscription,
  useCancelSubscription,
  useValidatePromoCode,
} from "../services/mutations";
import { getSubscriptionComparison } from "../services/api";
import { useActiveSubscriptionQuery } from "../services/queries";
import { useQueryClient } from "react-query";

const SUBSCRIPTION_ACTIONS = {
  SUBSCRIBE: "SUBSCRIBE",
  UPGRADE: "UPGRADE",
  DOWNGRADE: "DOWNGRADE",
  CANCEL: "CANCEL",
  ACTIVE: "ACTIVE",
};

const SUBSCRIPTION_TIERS = {
  FREE: 0,
};

const BILLING_CYCLES = {
  ANNUAL: 0,
  MONTHLY: 1,
};

const SUBSCRIPTION_LIMITS = {
  ANNUAL: 40,
  MONTHLY: 10,
};

const DEFAULT_FREE_PLAN = {
  type: "free",
  name: "Free",
  price: "0",
  validityDate: "N/A",
};

export const useSubscription = ({ enabled = true }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {
    data: activeSubscription,
    isLoading: isSubscriptionLoading,
    refetch: refetchSubscription,
  } = useActiveSubscriptionQuery({
    enabled: enabled,
  });

  // State management
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [defaultCardId, setDefaultCardId] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comparePlan, setComparePlan] = useState(false);
  const [features, setFeatures] = useState(null);
  const [packages, setPackages] = useState([]);

  // Promo code related state
  const [promoCode, setPromoCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);

  // Service hooks
  const purchaseSubscription = usePurchaseSubscription();
  const changeSubscription = useChangeSubscription();
  const cancelSubscription = useCancelSubscription();
  const validatePromoCodeMutation = useValidatePromoCode();

  // Get subscription comparison data
  const fetchComparisonData = useCallback(async (options = {}) => {
    try {
      const response = await getSubscriptionComparison(options);
      if (response.data.success) {
        setFeatures(response.data.data.features);
        setPackages(response.data.data.packages);
        setComparePlan(true);
        return {
          features: response.data.data.features,
          packages: response.data.data.packages,
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      return null;
    }
  }, []);

  // Handle payment method change
  const handlePaymentMethodChange = useCallback((cardId) => {
    setDefaultCardId(cardId);
  }, []);

  // Reset discount code state
  const resetDiscountCode = useCallback(() => {
    setPromoCode("");
    setDiscountInfo(null);
    setCodeError("");
    setValidatingCode(false);
  }, []);

  // Close all modals
  const closeAllModals = useCallback(() => {
    setShowUpgradeModal(false);
    setShowDowngradeModal(false);
    setSelectedPlan(null);
    resetDiscountCode();
  }, [resetDiscountCode]);

  // Format subscription data for display
  const prepareSubscriptionData = useCallback((subscription) => {
    if (!subscription) return null;

    return {
      type: subscription.name.toLowerCase(),
      name: subscription.name,
      price: subscription.per_month_cost,
      validityDate: subscription.valid_upto
        ? moment(subscription.valid_upto).format("MMM D, YYYY")
        : null,
      durationDisplay: subscription.price?.duration_display,
      yearlyPrice: subscription.price?.price,
      price_id: subscription.price?.stripe_price_id,
      product_id: subscription.stripe_product_id,
    };
  }, []);

  // Get next day after current plan
  const getNextDayAfterCurrentPlan = useCallback(() => {
    if (!activeSubscription?.valid_upto) return "Today";
    return moment(activeSubscription.valid_upto)
      .add(1, "days")
      .format("MMM D, YYYY");
  }, [activeSubscription]);

  // Extract list items from HTML string
  const extractListItems = useCallback((htmlString) => {
    if (!htmlString) return [];

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    return Array.from(tempDiv.querySelectorAll("li")).map(
      (li) => li.textContent
    );
  }, []);

  // Validate promo code
  const handleApplyDiscountCode = useCallback(
    (code) => {
      // If code is null, it means the user wants to remove the discount
      if (code === null || code === "") {
        setCodeError("")
        resetDiscountCode();
        return;
      }

      setPromoCode(code);
      setValidatingCode(true);

      // Reset previous discount info and errors
      setDiscountInfo(null);
      setCodeError("");

      // Check if we have a selected plan with required fields
      if (
        !selectedPlan?.price?.stripe_price_id ||
        !selectedPlan?.stripe_product_id
      ) {
        setValidatingCode(false);
        setCodeError(
          "Cannot validate discount code due to missing plan information"
        );
        console.error("Missing required fields for discount validation:", {
          price_id: selectedPlan?.price?.stripe_price_id,
          product_id: selectedPlan?.stripe_product_id,
        });
        return;
      }

      validatePromoCodeMutation.mutate(
        {
          price_id: selectedPlan.price.stripe_price_id,
          product_id: selectedPlan.stripe_product_id,
          promo_code: code,
        },
        {
          onSuccess: (response) => {
            setValidatingCode(false);
            if (response?.data?.success && response?.data?.data?.valid) {
              const data = response.data.data;
              const originalAmount = data.original_amount;
              const discountedAmount = data.discounted_amount;

              setDiscountInfo({
                code: data.discount_info.code,
                discountedPrice: discountedAmount,
                originalPrice: originalAmount,
                percentOff: data.discount_info.percent_off,
              });
            } else {
              setDiscountInfo(null);
              setCodeError(
                response?.data?.data?.message || "Invalid promo code"
              );
            }
          },
          onError: (error) => {
            setValidatingCode(false);
            setDiscountInfo(null);
            setCodeError("Failed to validate promo code");
            console.error("Promo code validation error:", error);
          },
        }
      );
    },
    [selectedPlan, validatePromoCodeMutation, resetDiscountCode]
  );

  // Handle subscription actions
  const handleActionPress = useCallback(
    (item) => {
      const { action } = item;

      // Set the selected plan first
      setSelectedPlan(item);

      // Reset any previous discount code info
      resetDiscountCode();

      // Handle different action types
      if (action === SUBSCRIPTION_ACTIONS.CANCEL) {
        // Show downgrade modal for cancellation to free tier
        setShowDowngradeModal(true);
      } else if (action === SUBSCRIPTION_ACTIONS.ACTIVE) {
        // Current plan, do nothing
        return;
      } else if (action === SUBSCRIPTION_ACTIONS.SUBSCRIBE) {
        // New subscription from free tier
        setShowUpgradeModal(true);
      } else if (action === SUBSCRIPTION_ACTIONS.UPGRADE) {
        // Upgrade from current tier to higher tier
        setShowUpgradeModal(true);
      } else if (action === SUBSCRIPTION_ACTIONS.DOWNGRADE) {
        // Downgrade to a lower tier (not free)
        setShowDowngradeModal(true);
      }
    },
    [resetDiscountCode]
  );

  // Replace dispatch(getActiveSubscription()) with refetchSubscription()
  const refreshSubscriptionData = useCallback(() => {
    refetchSubscription();
    // Invalidate userEligibility query to trigger refetch
    queryClient.invalidateQueries("userEligibility");
  }, [refetchSubscription, queryClient]);

  // Handle purchase subscription
  const handlePurchaseSubscription = useCallback(
    (
      productId,
      priceId,
      promoCode,
      source = "settings",
      onSuccess,
      onError
    ) => {
      if (!productId || !priceId) {
        console.error("Invalid product or price ID");
        return;
      }

      setLoading(true);
      purchaseSubscription.mutateAsync(
        {
          product_id: productId,
          price_id: priceId,
          source: source,
          // payment_method: defaultCardId,
          promo_code: promoCode,
        },
        {
          onSuccess: (res) => {
            setLoading(false);
            if (res?.data?.data?.url) {
              window.open(res.data.data.url, "_blank");
            }
            closeAllModals();
            refreshSubscriptionData();
            if (onSuccess) onSuccess(res);
          },
          onError: (error) => {
            console.error("Purchase subscription error:", error);
            setLoading(false);
            if (onError) onError(error);
          },
        }
      );
    },
    [
      purchaseSubscription,
      // defaultCardId,
      closeAllModals,
      refreshSubscriptionData,
    ]
  );

  // Handle change subscription
  const handleChangeSubscription = useCallback(
    (productId, priceId, promoCode, onSuccess, onError, isUpgrade = false) => {
      if (!productId || !priceId) {
        console.error("Invalid product or price ID");
        return;
      }

      setLoading(true);
      changeSubscription.mutateAsync(
        {
          product_id: productId,
          price_id: priceId,
          promo_code: promoCode,
        },
        {
          onSuccess: (res) => {
            setLoading(false);
            // Only redirect for upgrades (not downgrades)
            if (isUpgrade && res?.data?.data?.url) {
              window.open(res.data.data.url, "_blank");
            }
            closeAllModals();
            refreshSubscriptionData();
            if (onSuccess) onSuccess(res);
          },
          onError: (error) => {
            console.error("Change subscription error:", error);
            setLoading(false);
            if (onError) onError(error);
          },
        }
      );
    },
    [changeSubscription, closeAllModals, refreshSubscriptionData]
  );

  // Handle cancel subscription
  const handleCancelSubscription = useCallback(
    (onSuccess, onError) => {
      setLoading(true);
      cancelSubscription.mutateAsync(
        {
          // payment_method: defaultCardId,
        },
        {
          onSuccess: (res) => {
            setLoading(false);
            closeAllModals();
            refreshSubscriptionData();
            if (onSuccess) onSuccess(res);
          },
          onError: (error) => {
            console.error("Cancel subscription error:", error);
            setLoading(false);
            if (onError) onError(error);
          },
        }
      );
    },
    [cancelSubscription, closeAllModals, refreshSubscriptionData]
  );

  // Handle confirm action
  const handleConfirmAction = useCallback(
    (params = {}) => {
      if (!selectedPlan) return;

      const { action, stripe_product_id, price } = selectedPlan;
      const { promoCode, source, onSuccess, onError } = params;

      if (action === SUBSCRIPTION_ACTIONS.CANCEL) {
        // Downgrade to free tier
        handleCancelSubscription(onSuccess, onError);
      } else if (action === SUBSCRIPTION_ACTIONS.SUBSCRIBE) {
        // New subscription
        handlePurchaseSubscription(
          stripe_product_id,
          price?.stripe_price_id,
          promoCode,
          source,
          onSuccess,
          onError
        );
      } else if (
        action === SUBSCRIPTION_ACTIONS.UPGRADE ||
        action === SUBSCRIPTION_ACTIONS.DOWNGRADE
      ) {
        // Upgrade to higher tier or downgrade to lower tier (not free)
        handleChangeSubscription(
          stripe_product_id,
          price?.stripe_price_id,
          promoCode,
          onSuccess,
          onError,
          action === SUBSCRIPTION_ACTIONS.UPGRADE
        );
      }
    },
    [
      selectedPlan,
      handleCancelSubscription,
      handlePurchaseSubscription,
      handleChangeSubscription,
    ]
  );

  // Gets downgrade warning items
  const getDowngradeWarningItems = useCallback(() => {
    return [
      "All Referrals will be permanently forfeited unless you resubscribe to a Premium or higher subscription level within 30 days.",
      "You'll lose access to premium features immediately after the downgrade.",
      "Your data will be preserved, but you may not be able to edit content that used premium features.",
    ];
  }, []);

  // Get subscription plan data
  const getSubscriptionPlans = useCallback(
    (fromPlan = true, toPlan = true, isDowngradeToFree = false) => {
      const isUpgrade = selectedPlan?.action === SUBSCRIPTION_ACTIONS.UPGRADE;

      // Prepare from plan data with different validity for upgrade vs downgrade
      const fromPlanData = fromPlan
        ? {
            ...prepareSubscriptionData(activeSubscription),
            validityDate: isUpgrade ? "Today" : prepareSubscriptionData(activeSubscription)?.validityDate,
          } || DEFAULT_FREE_PLAN
        : null;

      // Create toPlanData with different starting dates for upgrade vs downgrade
      const toPlanData =
        toPlan && selectedPlan
          ? {
              ...prepareSubscriptionData(selectedPlan),
              validityDate: isUpgrade ? "Today" : getNextDayAfterCurrentPlan(),
            }
          : null;

      // Create free plan with correct starting date
      const freePlanData = {
        ...DEFAULT_FREE_PLAN,
        validityDate: getNextDayAfterCurrentPlan(),
      };

      return {
        fromPlanData,
        toPlanData: isDowngradeToFree ? freePlanData : toPlanData,
        freePlanData,
      };
    },
    [
      activeSubscription,
      selectedPlan,
      prepareSubscriptionData,
      getNextDayAfterCurrentPlan,
    ]
  );

  // Get limits for billing cycles
  const getSubscriptionLimits = useCallback((billingCycle) => {
    return billingCycle === BILLING_CYCLES.ANNUAL
      ? SUBSCRIPTION_LIMITS.ANNUAL
      : SUBSCRIPTION_LIMITS.MONTHLY;
  }, []);

  return {
    // Constants
    SUBSCRIPTION_ACTIONS,
    BILLING_CYCLES,
    SUBSCRIPTION_TIERS,

    // State
    selectedPlan,
    defaultCardId,
    showUpgradeModal,
    showDowngradeModal,
    loading,
    comparePlan,
    features,
    packages,
    promoCode,
    discountInfo,
    codeError,
    validatingCode,
    isSubscriptionLoading,

    // Setters
    setSelectedPlan,
    setShowUpgradeModal,
    setShowDowngradeModal,
    setComparePlan,
    setPackages,

    // Utility functions
    getSubscriptionLimits,
    prepareSubscriptionData,
    getNextDayAfterCurrentPlan,
    extractListItems,
    getSubscriptionPlans,
    getDowngradeWarningItems,

    // Actions
    handlePaymentMethodChange,
    handleApplyDiscountCode,
    resetDiscountCode,
    closeAllModals,
    handleActionPress,
    handleConfirmAction,
    fetchComparisonData,
    handlePurchaseSubscription,
    handleChangeSubscription,
    handleCancelSubscription,
    refreshSubscriptionData,
  };
}; 